import { getUserId } from "src/utils/auth";
import { CreatedResponse, request } from ".";
import { Id } from "src/utils/types";

export type Service = {
  id_atendimento: number;
  service_id?: number;
  schedule_id?: number;
  tipo_atendimento?: string;
  status_consulta?: string;
  id_colaborador: number;
  id_empresa: number;
  id_psicologo: number;
  colaborador: string;
  atendimento: string;
  area: string;
  setor: string;
  empresa: string;
  cidade: string;
  observacoes: string;
  queixa_inicial: string;
  psicologo: string;
  pago?: boolean;
  // do ponto de vista do front, continuamos usando string
  service_status?: string;
};

export type ServiceStatus = "Realizado" | "Paciente Faltou";

export type CreateServicePayload = {
  schedule_id?: number | string;
  collaborator_id: number | string;
  company_id: number | string;
  area: string;
  department?: string;
  city: string;
  date_of_service: string;
  annotations: string;
  complaint: string;
  edit_reason?: string;
  service_status?: ServiceStatus;
  status_consulta?: "cancelada" | string;
};

export type UpdateServicePayload = CreateServicePayload & {};

export type GetAllServicesSummary = {
  qtde_atendimentos: number;
  empresas: string[];
  colaboradores_atendidos: string[];
  psicologos: string[];
};

export type GetAllServicesResponse = {
  summary: GetAllServicesSummary;
  services: Service[];
};

const getFirstFilledValue = (...values: unknown[]): unknown =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const normalizeId = (...values: unknown[]): number => {
  const value = getFirstFilledValue(...values);
  const numericValue = Number(value);

  return Number.isFinite(numericValue) ? numericValue : 0;
};

// 🔧 helper: normaliza payloads antigos e novos da API para o formato usado pelo front
export const normalizeServiceStatus = (service: any): Service => {
  const status =
    typeof service?.service_status === "object" && service?.service_status !== null
      ? service.service_status.status
      : service?.service_status;

  return {
    id_atendimento: normalizeId(service?.id_atendimento, service?.service_id, service?.id),
    service_id: normalizeId(service?.service_id, service?.id_atendimento, service?.id),
    schedule_id: normalizeId(service?.schedule_id, service?.scheduleId, service?.fk_schedule_id, service?.fkScheduleId),
    tipo_atendimento: service?.tipo_atendimento ?? service?.tipoAtendimento ?? service?.type ?? "",
    status_consulta: service?.status_consulta ?? service?.statusConsulta ?? service?.status_consulta,
    id_colaborador: normalizeId(
      service?.id_colaborador,
      service?.colaborador_id,
      service?.collaborator_id,
      service?.idColaborador,
      service?.collaboratorId,
      service?.fk_collaborator_id,
      service?.fkCollaboratorId,
      service?.colaborador?.id_colaborador,
      service?.colaborador?.colaborador_id,
      service?.colaborador?.id,
      service?.collaborator?.id_colaborador,
      service?.collaborator?.collaborator_id,
      service?.collaborator?.id
    ),
    id_empresa: normalizeId(
      service?.id_empresa,
      service?.company_id,
      service?.fk_company_id,
      service?.empresa?.id,
      service?.company?.id
    ),
    id_psicologo: normalizeId(
      service?.id_psicologo,
      service?.psychologist_id,
      service?.fk_psychologist_id,
      service?.psicologo?.id,
      service?.psychologist?.id
    ),
    colaborador: service?.colaborador ?? service?.collaborator_name ?? "",
    atendimento: service?.atendimento ?? service?.date_of_service ?? "",
    area: service?.area ?? "",
    setor: service?.setor ?? service?.department ?? "",
    empresa: service?.empresa ?? service?.trade_name ?? "",
    cidade: service?.cidade ?? service?.city ?? "",
    observacoes: service?.observacoes ?? service?.annotations ?? "",
    queixa_inicial: service?.queixa_inicial ?? service?.complaint ?? "",
    psicologo: service?.psicologo ?? service?.psychologist_name ?? "",
    pago: service?.pago ?? service?.paid,
    service_status: status != null ? status : undefined,
  };
};

export async function getServices(
  startDate: string,
  endDate: string,
  id: Id,
  q?: string
): Promise<Service[]> {
  const response = await request.get<Service[]>(`/psicologos/${id}/services`, {
    params: {
      start_date: startDate,
      end_date: endDate,
      ...(q ? { q } : {}),
    },
  });

  if (!response) {
    return [];
  }

  const data = response.data ?? [];
  return data.map(normalizeServiceStatus);
}

export async function createService(
  payload: CreateServicePayload
): Promise<CreatedResponse | null> {
  const id = getUserId();
  const response = await request.post<CreatedResponse>(
    `/psicologos/${id}/services`,
    payload
  );

  if (!response) {
    return null;
  }

  return response.data;
}

export const updateService = async (
  psychologistId: Id,
  serviceId: Id,
  payload: UpdateServicePayload
): Promise<void> => {
  await request.put(`/psicologos/${psychologistId}/services/${serviceId}`, payload);
};

export const getServiceById = async (
  psychologistId: Id,
  serviceId: Id
): Promise<Service> => {
  const response = await request.get<Service>(
    `/psicologos/${psychologistId}/services/${serviceId}`
  );

  return normalizeServiceStatus(response.data);
};

export const deleteServiceByID = async (
  psychologistId: Id,
  serviceId: Id,
  reason: string
): Promise<void> => {
  await request.delete(`/psicologos/${psychologistId}/services/${serviceId}`, {
    params: {
      reason: reason,
    },
  });
};

export type UpdateServicePaymentResult = {
  /**
   * true quando a quitação zerou as sessões em aberto do colaborador
   * bloqueado e a API o desbloqueou automaticamente.
   */
  colaborador_desbloqueado: boolean;
};

export const updateServicePaymentStatusByID = async (
  psychologistId: Id,
  serviceId: Id,
  paymentStatus: boolean,
  reason: string
): Promise<UpdateServicePaymentResult> => {
  const response = await request.patch<UpdateServicePaymentResult>(
    `/psicologos/${psychologistId}/services/${serviceId}/payment`,
    {
      paid: paymentStatus,
      reason: reason,
    }
  );

  return { colaborador_desbloqueado: Boolean(response?.data?.colaborador_desbloqueado) };
};

export type ServiceStatusPayload = {
  service_status?: ServiceStatus;
  status_consulta?: "cancelada" | string;
};

export const updateServiceStatusPayload = async (
  psychologistId: Id,
  serviceId: Id,
  payload: ServiceStatusPayload
): Promise<void> => {
  await request.patch(
    `/psicologos/${psychologistId}/services/${serviceId}/status`,
    payload
  );
};

export const updateServiceStatus = async (
  psychologistId: Id,
  serviceId: Id,
  status: ServiceStatus
): Promise<void> => {
  await updateServiceStatusPayload(psychologistId, serviceId, {
    service_status: status,
  });
};

export async function getAllServices(
  startDate: string,
  endDate: string
): Promise<GetAllServicesResponse> {
  const response = await request.get<GetAllServicesResponse>("/services/all", {
    params: {
      start_date: startDate,
      end_date: endDate,
    },
  });

  const original = response.data;

  return {
    ...original,
    services: (original.services ?? []).map(normalizeServiceStatus),
  };
}
